import React from 'react';
import { getIconComponent } from '@/pages/workflow/helper/icon-mapping';
import { cn } from '@/lib/utils';

interface IconTileProps {
  /** Icon name/identifier */
  icon: string;
  /** Background color (hex or CSS color) */
  backgroundColor: string;
  /** Icon color (defaults to white) */
  iconColor?: string;
  /** Size variant */
  size?: 'small' | 'large';
  /** Additional className for the container */
  className?: string;
  /** Additional icon className */
  iconClassName?: string;
  /** Inline styles for the container */
  style?: React.CSSProperties;
}

/**
 * IconTile component - displays an icon within a colored rounded square tile
 *
 * @example
 * // Small tile (32x32)
 * <IconTile icon="tool" backgroundColor="#4F46E5" size="small" />
 *
 * @example
 * // Large tile (40x40)
 * <IconTile icon="memory" backgroundColor="#10B981" size="large" />
 */
export const IconTile: React.FC<IconTileProps> = ({
  icon,
  backgroundColor,
  iconColor = '#ffffff',
  size = 'small',
  className,
  iconClassName,
  style,
}) => {
  const sizeConfig = {
    small: {
      container: 'w-8 h-8', // 32px
      icon: iconClassName || 'w-5! h-5!',
    },
    large: {
      container: 'w-10 h-10', // 40px
      icon: iconClassName || 'w-6 h-6',
    },
  };

  const config = sizeConfig[size];

  const styleHasBackground =
    style != null &&
    (style.background !== undefined || style.backgroundImage !== undefined);

  const containerStyle = {
    ...(backgroundColor && !styleHasBackground ? { backgroundColor } : {}),
    ...style,
  };

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-lg shrink-0',
        config.container,
        className,
      )}
      style={containerStyle}
    >
      {getIconComponent(icon, iconColor, config.icon)}
    </div>
  );
};
